import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Highlighter, Trash2, Edit3, Save, X, Eye, EyeOff, Search, Brain, Lightbulb, Star, Heart, MessageSquare, AlertCircle, Quote } from 'lucide-react';

interface Highlight {
  id: string;
  text: string;
  startOffset: number;
  endOffset: number;
  color: string;
  category: string;
  note?: string;
  tags: string[];
  createdAt: Date;
  updatedAt: Date;
  isPublic: boolean;
  isShared: boolean;
  likes: number;
  isLiked: boolean;
  author: string;
  authorId: string;
  parentElement: string;
  xpath: string;
}

interface HighlightCategory {
  id: string;
  name: string;
  color: string;
  icon: React.ComponentType<any>;
  description: string;
  isDefault: boolean;
  count: number;
}

interface SmartHighlightingProps {
  contentRef: React.RefObject<HTMLElement | null>;
  userId: string;
  onHighlightCreate?: (highlight: Highlight) => void;
  onHighlightUpdate?: (highlight: Highlight) => void;
  onHighlightDelete?: (highlightId: string) => void;
  onHighlightShare?: (highlight: Highlight) => void;
  className?: string;
}

const SmartHighlighting: React.FC<SmartHighlightingProps> = ({
  contentRef,
  userId,
  onHighlightCreate,
  onHighlightUpdate,
  onHighlightDelete,
  onHighlightShare,
  className = ''
}) => {
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectionRange, setSelectionRange] = useState<Range | null>(null);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [showCategories, setShowCategories] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [editingHighlight, setEditingHighlight] = useState<Highlight | null>(null);
  const [noteText, setNoteText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('important');
  const [selectedColor, setSelectedColor] = useState<string>('#fef3c7');
  const [showHighlights, setShowHighlights] = useState(true);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'category' | 'likes'>('date');
  const [viewMode, setViewMode] = useState<'inline' | 'sidebar'>('inline');
  const [searchQuery, setSearchQuery] = useState('');

  const highlightMenuRef = useRef<HTMLDivElement>(null);
  const noteInputRef = useRef<HTMLTextAreaElement>(null);

  const highlightCategories: HighlightCategory[] = [
    {
      id: 'important',
      name: 'Important',
      color: '#fef3c7',
      icon: Star,
      description: 'Key points and important information',
      isDefault: true,
      count: 0
    },
    {
      id: 'question',
      name: 'Question',
      color: '#dbeafe',
      icon: MessageSquare,
      description: 'Questions or areas needing clarification',
      isDefault: true,
      count: 0
    },
    {
      id: 'idea',
      name: 'Idea',
      color: '#dcfce7',
      icon: Lightbulb,
      description: 'Ideas and inspiration',
      isDefault: true,
      count: 0
    },
    {
      id: 'research',
      name: 'Research',
      color: '#f3e8ff',
      icon: Brain,
      description: 'Points for further research',
      isDefault: true,
      count: 0
    },
    {
      id: 'quote',
      name: 'Quote',
      color: '#fce7f3',
      icon: Quote,
      description: 'Notable quotes and passages',
      isDefault: true,
      count: 0
    },
    {
      id: 'criticism',
      name: 'Criticism',
      color: '#fee2e2',
      icon: AlertCircle,
      description: 'Critical points or disagreements',
      isDefault: true,
      count: 0
    }
  ];

  const colorPalette = [
    '#fef3c7', // Yellow
    '#dbeafe', // Blue
    '#dcfce7', // Green
    '#f3e8ff', // Purple
    '#fce7f3', // Pink
    '#fee2e2', // Red
    '#f0f9ff', // Light Blue
    '#f0fdf4', // Light Green
    '#fefce8', // Light Yellow
    '#fdf4ff', // Light Purple
    '#fff7ed', // Orange
    '#f8fafc'  // Gray
  ];

  // Handle text selection
  const handleTextSelection = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      setSelectedText('');
      setSelectionRange(null);
      setShowHighlightMenu(false);
      return;
    }

    const range = selection.getRangeAt(0);
    const text = selection.toString().trim();
    
    if (text.length > 0) {
      setSelectedText(text);
      setSelectionRange(range.cloneRange());
      setShowHighlightMenu(true);
    } else {
      setSelectedText('');
      setSelectionRange(null);
      setShowHighlightMenu(false);
    }
  }, []);

  // Create highlight
  const createHighlight = useCallback((categoryId: string, color: string, note?: string) => {
    if (!selectionRange || !contentRef.current) return;

    const category = highlightCategories.find(c => c.id === categoryId);
    if (!category) return;

    const highlight: Highlight = {
      id: Date.now().toString(),
      text: selectedText,
      startOffset: selectionRange.startOffset,
      endOffset: selectionRange.endOffset,
      color,
      category: categoryId,
      note: note || '',
      tags: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      isPublic: false,
      isShared: false,
      likes: 0,
      isLiked: false,
      author: 'Current User',
      authorId: userId,
      parentElement: selectionRange.startContainer.parentElement?.tagName || '',
      xpath: getXPath(selectionRange.startContainer)
    };

    setHighlights(prev => [...prev, highlight]);
    onHighlightCreate?.(highlight);
    
    // Clear selection
    window.getSelection()?.removeAllRanges();
    setSelectedText('');
    setSelectionRange(null);
    setShowHighlightMenu(false);
    setShowNotes(false);
    setNoteText('');
  }, [selectedText, selectionRange, contentRef, userId, onHighlightCreate, highlightCategories]);

  // Update highlight
  const updateHighlight = useCallback((highlightId: string, updates: Partial<Highlight>) => {
    setHighlights(prev => prev.map(h => 
      h.id === highlightId 
        ? { ...h, ...updates, updatedAt: new Date() }
        : h
    ));
    
    const updatedHighlight = highlights.find(h => h.id === highlightId);
    if (updatedHighlight) {
      onHighlightUpdate?.({ ...updatedHighlight, ...updates });
    }
  }, [highlights, onHighlightUpdate]);

  // Delete highlight
  const deleteHighlight = useCallback((highlightId: string) => {
    setHighlights(prev => prev.filter(h => h.id !== highlightId));
    onHighlightDelete?.(highlightId);
  }, [onHighlightDelete]);

  // Get XPath for element
  const getXPath = (element: Node): string => {
    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentNode!;
    }
    
    const path: string[] = [];
    while (element && element.nodeType === Node.ELEMENT_NODE) {
      let index = 0;
      let sibling = element.previousSibling;
      while (sibling) {
        if (sibling.nodeType === Node.ELEMENT_NODE && sibling.nodeName === element.nodeName) {
          index++;
        }
        sibling = sibling.previousSibling;
      }
      
      const tagName = element.nodeName.toLowerCase();
      const pathIndex = index > 0 ? `[${index + 1}]` : '';
      path.unshift(tagName + pathIndex);
      element = element.parentNode!;
    }
    
    return '/' + path.join('/');
  };

  // Render highlights on content
  useEffect(() => {
    if (!contentRef.current || !showHighlights) return;

    // Clear existing highlights
    const existingHighlights = contentRef.current.querySelectorAll('.smart-highlight');
    existingHighlights.forEach(el => el.remove());

    // Apply highlights
    highlights.forEach(highlight => {
      if (highlight.category === filterCategory || filterCategory === 'all') {
        applyHighlight(highlight);
      }
    });
  }, [highlights, showHighlights, filterCategory, contentRef]);

  // Apply highlight to content
  const applyHighlight = (highlight: Highlight) => {
    if (!contentRef.current) return;

    try {
      const xpathResult = document.evaluate(
        highlight.xpath,
        contentRef.current,
        null,
        XPathResult.FIRST_ORDERED_NODE_TYPE,
        null
      );
      
      const element = xpathResult.singleNodeValue;
      if (!element || element.nodeType !== Node.TEXT_NODE) return;

      const textNode = element as Text;
      const text = textNode.textContent || '';
      
      if (highlight.startOffset >= 0 && highlight.endOffset <= text.length) {
        const beforeText = text.substring(0, highlight.startOffset);
        const highlightText = text.substring(highlight.startOffset, highlight.endOffset);
        const afterText = text.substring(highlight.endOffset);
        
        const highlightSpan = document.createElement('span');
        highlightSpan.className = 'smart-highlight';
        highlightSpan.style.backgroundColor = highlight.color;
        highlightSpan.style.padding = '2px 4px';
        highlightSpan.style.borderRadius = '3px';
        highlightSpan.style.cursor = 'pointer';
        highlightSpan.style.position = 'relative';
        highlightSpan.textContent = highlightText;
        highlightSpan.dataset.highlightId = highlight.id;
        
        // Add click handler
        highlightSpan.addEventListener('click', (e) => {
          e.stopPropagation();
          setEditingHighlight(highlight);
          setNoteText(highlight.note || '');
          setShowNotes(true);
        });
        
        const parent = textNode.parentNode;
        if (parent) {
          const beforeNode = document.createTextNode(beforeText);
          const afterNode = document.createTextNode(afterText);
          
          parent.insertBefore(beforeNode, textNode);
          parent.insertBefore(highlightSpan, textNode);
          parent.insertBefore(afterNode, textNode);
          parent.removeChild(textNode);
        }
      }
    } catch (error) {
      console.error('Error applying highlight:', error);
    }
  };

  // Event listeners
  useEffect(() => {
    document.addEventListener('mouseup', handleTextSelection);
    document.addEventListener('keyup', handleTextSelection);
    
    return () => {
      document.removeEventListener('mouseup', handleTextSelection);
      document.removeEventListener('keyup', handleTextSelection);
    };
  }, [handleTextSelection]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (highlightMenuRef.current && !highlightMenuRef.current.contains(event.target as Node)) {
        setShowHighlightMenu(false);
        setShowCategories(false);
        setShowNotes(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtered and sorted highlights
  const filteredHighlights = highlights
    .filter(highlight => {
      const matchesCategory = filterCategory === 'all' || highlight.category === filterCategory;
      const matchesSearch = searchQuery === '' || 
        highlight.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        highlight.note?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'date':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'category':
          return a.category.localeCompare(b.category);
        case 'likes':
          return b.likes - a.likes;
        default:
          return 0;
      }
    });

  return (
    <div className={`smart-highlighting-container ${className}`}>
      {/* Highlight Menu */}
      {showHighlightMenu && selectedText && (
        <div
          ref={highlightMenuRef}
          className="fixed z-50 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[300px]"
          style={{
            top: selectionRange ? selectionRange.getBoundingClientRect().top - 60 : 0,
            left: selectionRange ? selectionRange.getBoundingClientRect().left : 0
          }}
        >
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Highlight Text</h3>
              <button
                onClick={() => setShowHighlightMenu(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
              "{selectedText.length > 100 ? selectedText.substring(0, 100) + '...' : selectedText}"
            </div>

            {/* Quick Categories */}
            <div className="grid grid-cols-3 gap-2">
              {highlightCategories.slice(0, 6).map(category => (
                <button
                  key={category.id}
                  onClick={() => createHighlight(category.id, category.color)}
                  className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: category.color }}
                  />
                  <category.icon className="w-4 h-4" />
                  <span className="text-xs font-medium">{category.name}</span>
                </button>
              ))}
            </div>

            {/* Custom Color */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Custom Color</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={selectedColor}
                  onChange={(e) => setSelectedColor(e.target.value)}
                  className="w-8 h-8 rounded border border-gray-300 dark:border-gray-600"
                />
                <button
                  onClick={() => createHighlight(selectedCategory, selectedColor)}
                  className="flex-1 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm"
                >
                  Highlight
                </button>
              </div>
            </div>

            {/* Add Note */}
            <button
              onClick={() => setShowNotes(true)}
              className="w-full flex items-center gap-2 p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Edit3 className="w-4 h-4" />
              <span className="text-sm">Add Note</span>
            </button>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showNotes && editingHighlight && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Note</h3>
              <button
                onClick={() => setShowNotes(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Highlighted Text</label>
                <div className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg text-sm">
                  "{editingHighlight.text}"
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Note</label>
                <textarea
                  ref={noteInputRef}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Add your thoughts about this highlight..."
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white resize-none"
                  rows={4}
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowNotes(false)}
                  className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    updateHighlight(editingHighlight.id, { note: noteText });
                    setShowNotes(false);
                    setEditingHighlight(null);
                    setNoteText('');
                  }}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  Save Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Highlights Sidebar */}
      {viewMode === 'sidebar' && (
        <div className="fixed right-0 top-0 h-full w-80 bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 shadow-lg z-40">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Highlights</h3>
              <button
                onClick={() => setShowHighlights(!showHighlights)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                {showHighlights ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
              </button>
            </div>
            
            {/* Search and Filters */}
            <div className="space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search highlights..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                />
              </div>
              
              <div className="flex gap-2">
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="all">All Categories</option>
                  {highlightCategories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  <option value="date">Date</option>
                  <option value="category">Category</option>
                  <option value="likes">Likes</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4">
            <div className="space-y-3">
              {filteredHighlights.map(highlight => {
                const category = highlightCategories.find(c => c.id === highlight.category);
                return (
                  <div
                    key={highlight.id}
                    className="p-3 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-4 h-4 rounded-full mt-1 flex-shrink-0" 
                        style={{ backgroundColor: highlight.color }}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {category && <category.icon className="w-4 h-4 text-gray-500" />}
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                            {category?.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {new Date(highlight.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-900 dark:text-white mb-2">
                          "{highlight.text}"
                        </p>
                        
                        {highlight.note && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded">
                            {highlight.note}
                          </p>
                        )}
                        
                        <div className="flex items-center gap-2 mt-2">
                          <button
                            onClick={() => {
                              updateHighlight(highlight.id, { isLiked: !highlight.isLiked, likes: highlight.isLiked ? highlight.likes - 1 : highlight.likes + 1 });
                            }}
                            className={`p-1 rounded transition-colors ${
                              highlight.isLiked 
                                ? 'text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20' 
                                : 'text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700'
                            }`}
                          >
                            <Heart className={`w-4 h-4 ${highlight.isLiked ? 'fill-current' : ''}`} />
                          </button>
                          
                          <span className="text-xs text-gray-500">{highlight.likes}</span>
                          
                          <button
                            onClick={() => deleteHighlight(highlight.id)}
                            className="p-1 text-gray-400 hover:text-red-500 hover:bg-gray-50 dark:hover:bg-gray-700 rounded transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              
              {filteredHighlights.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Highlighter className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No highlights found</p>
                  <p className="text-sm">Select text to create highlights</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartHighlighting;
